/**
 * The owner's first week, as a checklist that marks itself.
 *
 * Every step is read from live data, so nothing can be ticked off without
 * actually doing it — and a logo deleted later puts the step back. It
 * disappears for good once the required steps are done.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Circle, Rocket } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../lib/api';

export const OnboardingChecklist = ({ onStatus }) => {
  const { user, authHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    // Owner only: every step is something only an owner can action.
    if (!user?.is_master_admin) return;
    axios.get(`${API}/onboarding/checklist`, { headers: authHeader() })
      .then(({ data: d }) => { setData(d); onStatus?.(d.complete); })
      .catch(() => { setData(null); onStatus?.(true); });
  }, [user, authHeader, onStatus]);

  if (!data || data.complete) return null;

  const pct = Math.round((data.done / data.total) * 100);

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5"
      data-testid="onboarding-checklist"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Rocket size={15} className="text-teal-600" /> Get your workshop set up
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.done} of {data.total} done. Each one takes a minute, and the
            paperwork looks right from the first invoice.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xl font-semibold text-slate-900 tabular-nums" data-testid="onboarding-percent">{pct}%</div>
        </div>
      </div>

      <div className="h-1.5 mt-3 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-teal-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ol className="mt-4 space-y-1">
        {data.steps.map((s) => (
          <li key={s.key} data-testid={`onboarding-step-${s.key}`}>
            <div className={`flex items-start gap-3 rounded-lg px-2.5 py-2 ${s.done ? '' : 'hover:bg-slate-50'}`}>
              <span className="mt-0.5 shrink-0">
                {s.done
                  ? <Check size={15} className="text-teal-600" />
                  : <Circle size={15} className="text-slate-300" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium ${s.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                  {s.title}
                  {s.optional && !s.done && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">optional</span>
                  )}
                </div>
                {!s.done && <div className="text-xs text-slate-500 mt-0.5">{s.detail}</div>}
              </div>
              {!s.done && (
                <Link
                  to={s.to}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800"
                  data-testid={`onboarding-cta-${s.key}`}
                >
                  {s.cta} <ChevronRight size={13} />
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default OnboardingChecklist;
